import { useState, useEffect, useRef } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Upload,
  Button,
  message,
  Select,
  Divider,
} from "antd";
import { UploadOutlined, CheckOutlined, PictureOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  createSlide,
  updateSlide,
  newImage,
} from "../../../redux/slices/frontpage";
import { normFile, beforeUpload, dummyRequest } from "../../../utils/images";
import Close from "../../../components/Close";
import styles from "../styles/Editor.module.css";

const { Option } = Select;

const Editor = ({ banner, closeEditor, handleBanner, isBannerOrPopUp, products, categories, initialFetch }) => {
  const [sending, handleSending] = useState(false);
  const [baseImg, handleBaseImg] = useState("");
  const [baseImg2, handleBaseImg2] = useState("");
  const [target, setTarget] = useState("take_out");
  const [action, handleAction] = useState('NONE');

  const platformSelectRef = useRef(null);

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      if (banner) {
        const newValues = {
          url: banner.action_type === "EXTERNAL" ? banner.action_value : "",
          category: banner.action_type === "CATEGORY" ? parseInt(banner.action_value) : "",
          product: banner.action_type === "PRODUCT" ? banner.action_value : "",
          sort: banner.sort || 0,
          name: banner.name,
          target: banner.mode === "APP" ? "app" : banner.mode === "KIOSCO" ? "kiosco" : "take_out",
          action: banner.action_type,
        };
        handleAction(banner.action_type);
        handleBaseImg(banner.image || "");
        handleBaseImg2(banner.image_movil || "");
        setTarget(banner.mode === "APP" ? "app" : banner.mode === "KIOSCO" ? "kiosco" : "take_out");
        form.setFieldsValue(newValues);
      } else {
        form.setFieldsValue({ target });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner]);

  const handleTargetChange = (value) => {
    setTarget(value);

    platformSelectRef.current?.blur();

    const invalidActionsForKiosco = ["MODAL", "EXTERNAL"];
    if (value === "kiosco" && invalidActionsForKiosco.includes(action)) {
      handleAction("NONE");
      form.setFieldsValue({ action: "NONE" });
    }
  };

  const uploadImage = async (valueImage, id, type) => {
    const isWeb = type === "web";
    const imagePreview = isWeb ? baseImg : baseImg2;
    const imageBanner = isWeb ? banner?.image : banner?.image_movil;
    if (imagePreview !== imageBanner || !banner) {
      const infoImage = new FormData();
      infoImage.append("id", id);
      infoImage.append("type", isWeb ? "image" : "image_movil");
      infoImage.append(
        "image",
        valueImage?.length ? valueImage[0]?.originFileObj : imagePreview
      );
      const response = await dispatch(newImage(infoImage));
      if (response.status === "success") {
        message.success("Cambios realizados con éxito");
        return response;
      } else {
        message.error("¡Hubo un problema! Inténtalo de nuevo");
        return false;
      }
    } else if (target === "app" || type === "mobile") {
      message.success("Cambios realizados con éxito");
      return false;
    }
  };

  const save = async () => {
    try {
      handleSending(true);
      const values = await form.validateFields();

      const needsWebImg = target !== "kiosco";
      const needsMobileImg = target === "take_out" || target === "kiosco";
      const hasWebImg = !needsWebImg || (baseImg && baseImg.length);
      const hasMobileImg = !needsMobileImg || (baseImg2 && baseImg2.length);

      if (hasWebImg && hasMobileImg) {
        const info = {
          type: isBannerOrPopUp === 'banner' ? 'BANNER' : 'POPUP',
          action,
          name: values.name,
          target: values.target === "app" || values.target === "kiosco" ? "take_out" : values.target,
          mode: values.target === "app" ? "APP" : values.target === "kiosco" ? "KIOSCO" : "WEB",
          sort: values.sort,
        };
        if (action === 'PRODUCT') info.action_value = values.product;
        if (action === 'EXTERNAL') info.action_value = values.url;
        if (action === 'CATEGORY') info.action_value = values.category;

        if (banner) {
          info.id = banner.id;
          info.status = banner.status;
        } else {
          info.status = 1;
        }

        if (banner) {
          const response = await dispatch(updateSlide(info));
          if (response.status === "success") {
            if (needsWebImg) await uploadImage(values.image, banner.id, "web");
            if (needsMobileImg) await uploadImage(values.image2, banner.id, "mobile");
          } else {
            message.error("¡Hubo un problema al subir el banner! Inténtalo de nuevo");
          }
        } else {
          const response = await dispatch(createSlide(info));
          if (response.status === "success") {
            let response_image;
            if (needsWebImg) {
              response_image = await uploadImage(values.image, response.banner.id, "web");
            }
            if (needsMobileImg) {
              response_image = await uploadImage(values.image2, response.banner.id, "mobile");
            }
            if (response_image?.banner) {
              handleBanner(response_image.banner);
            }
          } else {
            message.error("¡Hubo un problema! Inténtalo de nuevo");
          }
        }
        await initialFetch();
      } else {
        message.error("Selecciona una imagen válida");
      }

      handleSending(false);
    } catch (e) {
      console.log(e);
    }
  };

  const typeLabel = isBannerOrPopUp === 'banner' ? 'Banner' : 'Popup';

  const renderImagePreview = (imgSrc) => (
    <div className={`${styles.preview} ${imgSrc ? styles.previewWithImage : ''}`}>
      {imgSrc ? (
        <img src={imgSrc} alt="Preview" className={styles.previewImg} />
      ) : (
        <div className={styles.previewPlaceholder}>
          <PictureOutlined className={styles.previewIcon} />
          <span className={styles.previewText}>Sin imagen</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className={styles.header}>
        <span>
          {banner?.id ? `Editar ${typeLabel}` : `Agregar ${typeLabel}`}
        </span>
        <Close action={closeEditor} />
      </div>

      <Form
        form={form}
        name="product-editor"
        layout="vertical"
        autoComplete="off"
        colon={false}
        requiredMark={false}
        className={styles.form}
        onFinish={save}
        initialValues={{ promo: false, url: "" }}
      >
        {/* Section: General Info */}
        <Divider orientation="left" className={styles.sectionDivider}>
          <span className={styles.sectionTitle}>Información General</span>
        </Divider>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label={<span className={styles.label}>PLATAFORMA</span>}
              name="target"
              className={styles.itemColumn}
            >
              <Select
                ref={platformSelectRef}
                onChange={handleTargetChange}
                className={styles.input}
              >
                <Option value="take_out">Sitio Web</Option>
                <Option value="app">App móvil</Option>
                {isBannerOrPopUp === 'banner' && (
                  <Option value="kiosco">Kiosco</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label={<span className={styles.label}>NOMBRE</span>}
              name="name"
              className={styles.itemColumn}
              rules={[{ required: true, message: "Ingresar nombre" }]}
            >
              <Input className={styles.input} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={<span className={styles.label}>POSICIÓN</span>}
              name="sort"
              className={styles.itemColumn}
              rules={[{ required: true, message: "Ingresar posición" }]}
            >
              <Input className={styles.input} type="number" />
            </Form.Item>
          </Col>
        </Row>

        {/* Section: Images */}
        <Divider orientation="left" className={styles.sectionDivider}>
          <span className={styles.sectionTitle}>Imágenes</span>
        </Divider>

        <Row gutter={24}>
          {target !== "kiosco" && (
            <Col span={12}>
              <div className={styles.imageCard}>
                <span className={styles.imageCardTitle}>
                  Imagen {typeLabel} (Desktop)
                </span>
                {renderImagePreview(baseImg)}
                <Form.Item
                  name="image"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  extra={<span className={styles.center}>(jpg/png — 1600×700px)</span>}
                  className={styles.uploadItem}
                >
                  <Upload
                    showUploadList={false}
                    customRequest={(e) => dummyRequest(e, handleBaseImg)}
                    beforeUpload={beforeUpload}
                    className={styles.upload}
                  >
                    <Button icon={<UploadOutlined />} className={styles.uploadImage} block>
                      Seleccionar imagen
                    </Button>
                  </Upload>
                </Form.Item>
              </div>
            </Col>
          )}
          {(target === "take_out" || target === "kiosco") && (
            <Col span={12}>
              <div className={styles.imageCard}>
                <span className={styles.imageCardTitle}>
                  Imagen {typeLabel} (Móvil)
                </span>
                {renderImagePreview(baseImg2)}
                <Form.Item
                  name="image2"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  extra={<span className={styles.center}>(jpg/png — 960×1500px)</span>}
                  className={styles.uploadItem}
                >
                  <Upload
                    showUploadList={false}
                    customRequest={(e) => dummyRequest(e, handleBaseImg2)}
                    beforeUpload={beforeUpload}
                    className={styles.upload}
                  >
                    <Button icon={<UploadOutlined />} className={styles.uploadImage} block>
                      Seleccionar imagen
                    </Button>
                  </Upload>
                </Form.Item>
              </div>
            </Col>
          )}
        </Row>

        {/* Section: Action */}
        <Divider orientation="left" className={styles.sectionDivider}>
          <span className={styles.sectionTitle}>Acción al hacer clic</span>
        </Divider>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="action"
              label={<span className={styles.label}>ACCIÓN</span>}
              rules={[{ required: true, message: "Seleccione una acción" }]}
            >
              <Select
                className={styles.input}
                onChange={(val) => handleAction(val)}
              >
                <Option value="NONE">Ninguna</Option>
                <Option value="PRODUCT">Producto</Option>
                <Option value="CATEGORY">Categoría</Option>
                {isBannerOrPopUp === 'banner' && target !== "kiosco" && (
                  <Option value="MODAL">Modal</Option>
                )}
                {target !== "kiosco" && (
                  <Option value="EXTERNAL">URL Externa</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {action === 'PRODUCT' && (
              <Form.Item name="product" label={<span className={styles.label}>PRODUCTO</span>}>
                <Select
                  options={products.map((item) => ({ value: item.sku, label: item.name }))}
                  filterOption
                  optionFilterProp="label"
                  showSearch
                />
              </Form.Item>
            )}
            {action === 'CATEGORY' && (
              <Form.Item name="category" label={<span className={styles.label}>CATEGORÍA</span>}>
                <Select
                  options={categories.map((item) => ({ value: item.value, label: item.text }))}
                />
              </Form.Item>
            )}
            {action === 'EXTERNAL' && target === "take_out" && (
              <Form.Item
                label={<span className={styles.label}>ENLACE</span>}
                name="url"
                className={styles.itemColumn}
              >
                <Input
                  className={styles.input}
                  placeholder="https://gobuff.com/enlace"
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Divider />

        <div className={styles.submitWrap}>
          <Form.Item className={styles.itemSubmit}>
            <Button
              loading={sending}
              className={styles.submit}
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
            >
              GUARDAR {typeLabel.toUpperCase()}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default Editor;